import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { getBadges } from '@/services/endpoints/badges'
import { useMenuStore } from '@/stores/menu'
import { useUserStore } from '@/stores/user'
import type { GroupBadges } from '@/types/badges'
import type { DefCallCounts } from '@/types/defCalls'
import type { PushCounts } from '@/types/pushRequests'

/** Route paths of the pages whose menu entries carry a badge. */
export const INCOMINGS_PATH = '/incomings'
export const RECAP_INCOMINGS_PATH = '/recap-incomings'
export const MY_PLAN_PATH = '/plans/your-plan'
export const DEF_CALLS_PATH = '/def-call'
export const PUSH_REQUESTS_PATH = '/push-requests'

/**
 * How old the badges may get before a page change asks again. A page change
 * used to refresh them every time, with five requests: moving quickly
 * between pages ran into the API's rate limit. Within a minute a page change
 * now costs nothing; whatever the user does themselves (a declaration, a
 * shipment, a new attack) refreshes them at once.
 */
export const STALE_MS = 60_000

/** Active def calls per type: the def call page's tabs read them too. */
export const defCallCounts = ref<DefCallCounts | null>(null)
/** Active push requests and those under the user's quota: the list's banner reads them too. */
export const pushCounts = ref<PushCounts | null>(null)

let last: GroupBadges | null = null
let lastAt = 0
let inFlight: Promise<void> | null = null
let again = false

/**
 * Writes the badges on the menu entries. Only on the entries the menu shows:
 * a count for a page the user can't open would sit on nothing. The recap is
 * `null` for whoever isn't admin/def of the group, and so is its badge.
 */
function apply(badges: GroupBadges | null): void {
  const menu = useMenuStore()
  const shown = (path: string, count: number | null | undefined): number | null =>
    badges && menu.isAllowed(path) ? (count ?? null) : null

  defCallCounts.value = badges?.def_calls ?? null
  pushCounts.value = badges?.push_requests ?? null
  menu.setLocalCount(INCOMINGS_PATH, shown(INCOMINGS_PATH, badges?.incomings))
  menu.setLocalCount(RECAP_INCOMINGS_PATH, shown(RECAP_INCOMINGS_PATH, badges?.incomings_recap))
  menu.setLocalCount(MY_PLAN_PATH, shown(MY_PLAN_PATH, badges?.my_plans))
  menu.setLocalCount(DEF_CALLS_PATH, shown(DEF_CALLS_PATH, badges?.def_calls.total))
  menu.setLocalCount(PUSH_REQUESTS_PATH, shown(PUSH_REQUESTS_PATH, badges?.push_requests?.active))
}

async function fetchOnce(): Promise<void> {
  const user = useUserStore()
  const group = user.selectedGroup
  if (!group) return
  try {
    const badges = await getBadges(group.id)
    // A group switch during the request must not paint the old group's counts
    // (the switch asks again for the new one).
    if (user.selectedGroup?.id !== group.id) return
    last = badges
    lastAt = Date.now()
    apply(badges)
  } catch {
    // Silent request, best effort: keep whatever the badges showed.
  }
}

/**
 * Re-reads every badge of the selected group with **one** request.
 *
 * - Default: always asks — call it after anything the user changed (it is
 *   what makes their own action show up at once).
 * - `ifStale`: asks only when the last answer, for this group, is older than
 *   `STALE_MS` — what page changes and a tab coming back into view use.
 *
 * Calls that overlap share one request; a forced call arriving while one is
 * in flight runs once more after it, since that answer may predate the
 * change it was called for.
 */
export function refreshBadges(options: { ifStale?: boolean } = {}): Promise<void> {
  const user = useUserStore()
  const group = user.selectedGroup
  if (!group) {
    last = null
    lastAt = 0
    apply(null)
    return Promise.resolve()
  }
  if (options.ifStale && last?.group_id === group.id && Date.now() - lastAt < STALE_MS) {
    return Promise.resolve()
  }
  if (inFlight) {
    if (!options.ifStale) again = true
    return inFlight
  }
  inFlight = (async () => {
    try {
      do {
        again = false
        await fetchOnce()
      } while (again)
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}

/** Test hook: forget the last answer, as on a fresh page load. */
export function resetBadges(): void {
  last = null
  lastAt = 0
  inFlight = null
  again = false
  defCallCounts.value = null
  pushCounts.value = null
}

/**
 * Mounted once by the app layout. Asks on start and on a group or role
 * switch; on a page change or when the tab comes back only if stale. Never on
 * a timer: the pages that need live data refresh their own list.
 *
 * When the menu arrives or changes, the last answer is re-applied without a
 * new request: which entries show a badge is the menu's business, not the
 * server's.
 */
export function useGroupBadges(): void {
  const user = useUserStore()
  const menu = useMenuStore()
  const route = useRoute()

  watch(
    () => [user.selectedGroup?.id ?? null, user.roleInSelectedGroup].join('|'),
    () => void refreshBadges(),
    { immediate: true },
  )

  const allowed = computed(() =>
    [INCOMINGS_PATH, RECAP_INCOMINGS_PATH, MY_PLAN_PATH, DEF_CALLS_PATH, PUSH_REQUESTS_PATH]
      .map((path) => menu.isAllowed(path))
      .join('|'),
  )
  watch(allowed, () => {
    if (last && last.group_id === user.selectedGroup?.id) apply(last)
  })

  // `path`, not `fullPath`: a tab or a filter written into the query string is not a page change.
  watch(
    () => route.path,
    () => void refreshBadges({ ifStale: true }),
  )

  function onVisibilityChange(): void {
    if (document.visibilityState === 'visible') void refreshBadges({ ifStale: true })
  }
  onMounted(() => document.addEventListener('visibilitychange', onVisibilityChange))
  onBeforeUnmount(() => document.removeEventListener('visibilitychange', onVisibilityChange))
}
